import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { deriveHypotheses, sunSign } from "@omina/core";
import type { Goal, PersonalityInputs } from "@omina/core";

import { CompanyStep, type CompanyFields } from "@/components/intake/CompanyStep";
import { YouStep, type YouFields } from "@/components/intake/YouStep";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  useChart,
  useGenerateMap,
  useProfile,
  useSaveProfile,
  useSetActiveGoal,
  useSettings,
} from "@/lib/queries";

/**
 * Intake, in two layers: first you, then your company.
 *
 * It used to be four screens in a fixed line, and the shape was the problem
 * rather than the length. Two of those four asked for personal data — a birth
 * time, a birth place — before the product had returned anything at all, so the
 * founder who only wanted to see her rooms had to hand over her birth
 * certificate to get there.
 *
 * The two layers are the real division: who *she* is does not change when she
 * pivots, and everything about the company does. Splitting on that line means a
 * pivot re-asks the second layer and leaves the first alone.
 *
 * Within layer one, quick and complete are both real answers. See YouStep for
 * why that is a change to rule #8 rather than a violation of it.
 */
export const Route = createFileRoute("/intake")({
  component: Intake,
});

// ─────────────────────────────────────────────────────────────
// Reading the chart answer
//
// `POST /api/chart` declares `astro` as `Record<string, unknown>`: the route
// assembles it conditionally — the moon carries a caveat only when there is no
// birth time, rising exists only with a time *and* a place — so the shape is
// built at runtime and the RPC client cannot infer it.
// ─────────────────────────────────────────────────────────────

interface AstroPoint {
  sign: string;
  position: string;
  element?: string;
  caveat?: string;
}

function astroPoint(astro: Record<string, unknown>, key: string): AstroPoint | null {
  const raw = astro[key];
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.sign !== "string" || typeof o.position !== "string") return null;
  return {
    sign: o.sign,
    position: o.position,
    ...(typeof o.element === "string" ? { element: o.element } : {}),
    ...(typeof o.caveat === "string" ? { caveat: o.caveat } : {}),
  };
}

// ─────────────────────────────────────────────────────────────
// Dictation — the Web Speech API, feature-detected. It exists in Chrome and
// Safari and does not exist in Firefox, so the button is simply not rendered
// where the API is missing: a dead microphone is worse than no microphone.
// ─────────────────────────────────────────────────────────────

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult:
    | ((event: {
        resultIndex: number;
        results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
      }) => void)
    | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
}

function getSpeechRecognition(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as
    | (new () => SpeechRecognitionLike)
    | null;
}

const STEPS = [
  { n: 1, title: "First, you", sub: "who is doing the work" },
  { n: 2, title: "Then, your company", sub: "who it is for" },
] as const;

function Intake() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [mode, setMode] = useState<"quick" | "complete">("quick");

  const chart = useChart();
  const profile = useProfile();
  const settings = useSettings();
  const saveProfile = useSaveProfile();
  const setActiveGoal = useSetActiveGoal();
  const generateMap = useGenerateMap();

  const [you, setYou] = useState<YouFields>({
    name: "",
    city: "",
    goal: null,
    fullName: "",
    birthDate: "",
    birthTime: "",
    birthPlace: "",
    enneagram: "",
    mbti: "",
    temperament: "",
  });
  const [company, setCompany] = useState<CompanyFields>({
    targetIndustry: "",
    audience: null,
    workedInIndustry: null,
    buyerRole: "",
    buyerCompany: "",
    buyerPain: "",
    story: "",
  });

  /**
   * Open with what she already answered.
   *
   * Intake is the *author* of these questions — the radar and the sidebar show
   * them and link back here to change them — so it has to be an editor, not a
   * one-shot wizard. Without this, "change your industry" would hand her an
   * empty form and quietly blank her name, her city and her product story on
   * save, because the endpoint replaces the whole row.
   *
   * Seeded exactly once, guarded by a ref rather than by a dependency list: the
   * profile query refetches after every save, and re-seeding on that would
   * overwrite whatever she has typed since.
   */
  const seeded = useRef(false);
  useEffect(() => {
    const row = profile.data;
    if (!row || seeded.current) return;
    seeded.current = true;

    setYou((f) => ({
      ...f,
      // The track lives on the org, not the profile — it is the one answer here
      // that is shared rather than personal.
      goal: f.goal ?? settings.data?.activeGoal ?? null,
      name: row.name ?? "",
      city: row.location ?? "",
      fullName: row.fullName ?? "",
      birthDate: row.birthDate ?? "",
      birthTime: row.birthTime ?? "",
      birthPlace: row.birthPlace ?? "",
      enneagram: row.personality?.enneagram ?? "",
      mbti: row.personality?.mbti ?? "",
      temperament: row.personality?.temperament ?? "",
    }));
    setCompany((f) => ({
      ...f,
      targetIndustry: row.targetIndustry ?? "",
      audience: row.audience === "b2b" || row.audience === "b2c" ? row.audience : null,
      workedInIndustry:
        row.industryOutsider === null || row.industryOutsider === undefined
          ? null
          : !row.industryOutsider,
      buyerRole: row.buyerRole ?? "",
      buyerCompany: row.buyerCompany ?? "",
      buyerPain: row.buyerPain ?? "",
      story: row.product ?? "",
    }));
  }, [profile.data]);

  const setYouField = <K extends keyof YouFields>(key: K, value: YouFields[K]) =>
    setYou((f) => ({ ...f, [key]: value }));
  const setCompanyField = <K extends keyof CompanyFields>(key: K, value: CompanyFields[K]) =>
    setCompany((f) => ({ ...f, [key]: value }));

  // ── the chart, computed ~1s after the birth data is complete ──
  //
  // The debounce is not cosmetic: the place is geocoded, which is the only
  // network call in the chart path, and firing it per keystroke would send one
  // request per letter of "São Paulo".
  const computedKey = useRef("");
  useEffect(() => {
    const key = `${you.birthDate}|${you.birthTime}|${you.birthPlace.trim()}`;
    if (!you.birthDate || !you.birthPlace.trim()) return;
    if (computedKey.current === key) return;

    if (chart.data || chart.error) chart.reset();

    const timer = setTimeout(() => {
      computedKey.current = key;
      chart.mutate({
        birthDate: you.birthDate,
        ...(you.birthTime ? { birthTime: you.birthTime } : {}),
        birthPlace: you.birthPlace.trim(),
      });
    }, 900);
    return () => clearTimeout(timer);
    // `chart` is a stable mutation handle; re-running on its state would restart
    // the debounce every time a token of the answer lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [you.birthDate, you.birthTime, you.birthPlace]);

  // ── dictation ──
  const [listening, setListening] = useState(false);
  const recognition = useRef<SpeechRecognitionLike | null>(null);
  const [speechAvailable, setSpeechAvailable] = useState(false);

  useEffect(() => {
    setSpeechAvailable(Boolean(getSpeechRecognition()));
    return () => recognition.current?.stop();
  }, []);

  function toggleDictation() {
    if (listening) {
      recognition.current?.stop();
      return;
    }
    const Recognition = getSpeechRecognition();
    if (!Recognition) return;

    const rec = new Recognition();
    rec.lang = navigator.language || "en-US";
    rec.continuous = true;
    // Interim results would rewrite the textarea under her cursor; only final
    // transcripts are appended.
    rec.interimResults = false;
    rec.onresult = (event) => {
      let added = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.isFinal) added += `${result[0].transcript} `;
      }
      if (added) {
        setCompany((f) => ({ ...f, story: (f.story ? `${f.story} ` : "") + added.trim() }));
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognition.current = rec;
    setListening(true);
    rec.start();
  }

  const astro = chart.data?.astro as Record<string, unknown> | undefined;
  const sun = astro ? astroPoint(astro, "sun") : null;
  const rising = astro ? astroPoint(astro, "rising") : null;

  /**
   * What the engines computed, in the shape `deriveHypotheses` reads.
   *
   * Rule #4: these are doorways to elicitation, not verdicts. The self-reported
   * frameworks ride alongside the computed ones because both are the same kind
   * of thing — borrowed vocabulary for something already true.
   */
  const personality: PersonalityInputs = useMemo(() => {
    const band = you.birthDate ? sunSign(you.birthDate) : null;
    const hd = chart.data?.hd;
    return {
      ...(you.birthDate ? { birthDate: you.birthDate } : {}),
      ...(you.birthTime ? { birthTime: you.birthTime } : {}),
      ...(you.birthPlace ? { birthPlace: you.birthPlace } : {}),
      ...(you.enneagram ? { enneagram: you.enneagram } : {}),
      ...(you.mbti ? { mbti: you.mbti } : {}),
      ...(you.temperament ? { temperament: you.temperament } : {}),
      ...(sun?.sign ?? band?.sign ? { sunSign: sun?.sign ?? band?.sign } : {}),
      ...(sun?.element ?? band?.element ? { element: sun?.element ?? band?.element } : {}),
      ...(hd?.typeKey ? { humanDesign: hd.typeKey } : {}),
      ...(hd?.authority ? { hdAuthority: hd.authority } : {}),
      ...(hd?.profile ? { hdProfile: hd.profile } : {}),
      ...(hd?.sunGate ? { sunGate: hd.sunGate.gate } : {}),
      ...(hd?.throatGates ? { throatGates: hd.throatGates.map((t) => t.gate) } : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [you, chart.data]);

  /**
   * Step one is done when she has a name, a track and a city.
   *
   * The birth data is *not* required, in either mode — that is the whole point
   * of the quick briefing. The complete tab asks for it and explains what it
   * buys, and settings asks again later; neither blocks her from reaching a
   * radar that already works without it.
   *
   * **The city is required, and it is the one addition to that list.** It was
   * optional, on the reasoning that a required field is a wall and a founder
   * who leaves is worth less than one who arrives with a gap. That reasoning
   * was wrong about this particular gap: every other blank here degrades
   * something — a weaker draft, an unranked room — while a missing city
   * degrades the feed to *nothing at all*, because event calendars are matched
   * by region and there is nothing to match on. Onboarding was finishing by
   * handing her an empty product and no way to see why.
   *
   * It is also the cheapest answer on the screen now that the button fills it
   * in one tap, which is what makes requiring it a fair trade rather than a
   * wall.
   */
  const canLeaveStep1 =
    Boolean(you.name.trim()) && you.goal !== null && Boolean(you.city.trim());

  /**
   * Two answers, both load-bearing, and nothing else.
   *
   * The industry, because it is what gates the curated base — without it the
   * radar cannot be honest about coverage. And a line about what she builds,
   * because every draft is cut from it.
   *
   * Everything else on the step is optional by design. "Did you come from the
   * industry" used to be required and is one tap, but a required tap is still a
   * wall; the buyer blanks were never required, because a guess there aims every
   * search at the wrong person and a gap does not. The old 60-character floor on
   * the story is gone too — it turned an answer into a word count.
   */
  const canFinish =
    Boolean(company.targetIndustry.trim()) && company.story.trim().length >= 20;

  const finishing = saveProfile.isPending || setActiveGoal.isPending || generateMap.isPending;
  const finishError = saveProfile.error ?? setActiveGoal.error ?? generateMap.error;

  const chartSummary = chart.data
    ? [
        chart.data.hd?.type,
        sun?.sign ? `${sun.sign} sun` : null,
        rising?.sign ? `${rising.sign} rising` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  /**
   * The end of intake: write the profile, set the track, compose the map.
   *
   * Sequential rather than parallel because each step depends on the one before
   * — the map is composed from the profile, for the active track.
   */
  async function finish() {
    if (!you.goal) return;

    const patterns: Record<string, string> = {};
    for (const hypothesis of deriveHypotheses(personality)) {
      patterns[hypothesis.field] = hypothesis.text;
    }

    try {
      await saveProfile.mutateAsync({
        name: you.name.trim(),
        // `location` is the region key the radar matches calendars on.
        location: you.city.trim() || null,
        product: company.story.trim(),
        targetIndustry: company.targetIndustry.trim(),
        audience: company.audience,
        buyerRole: company.buyerRole.trim() || null,
        buyerCompany: company.buyerCompany.trim() || null,
        buyerPain: company.buyerPain.trim() || null,
        // The question is "did you come from it"; the qualifier is the inverse.
        // Null when unanswered rather than false-y: `!null` is `true`, which
        // would file every founder who skipped the question as an outsider and
        // make her drafts open by earning credibility she may already have.
        industryOutsider:
          company.workedInIndustry === null ? null : !company.workedInIndustry,
        fullName: you.fullName.trim() || null,
        birthDate: you.birthDate || null,
        birthTime: you.birthTime || null,
        birthPlace: you.birthPlace.trim() || null,
        patterns,
      });
      await setActiveGoal.mutateAsync(you.goal);
      await generateMap.mutateAsync(you.goal);
      // The radar, not the map — it is the one screen now, and it is where the
      // answer to everything she just typed shows up.
      await navigate({ to: "/radar" });
    } catch {
      // Whichever step failed is showing its own message under the button; she
      // stays here with every answer intact.
    }
  }

  const current = STEPS[step - 1]!;

  return (
    <div className="space-y-7 pb-16">
      <header className="space-y-3">
        <p className="text-2xs tracking-[0.14em] text-muted-foreground/70 uppercase">
          Who am I · step {step} of 2
        </p>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight">
          {current.title}
        </h1>
        <p className="text-base text-muted-foreground">{current.sub}</p>

        {/* Two segments rather than a percentage: with only two steps a bar
            reads as a progress lie, and the labels are the useful part. */}
        <div className="flex gap-2 pt-1" aria-hidden>
          {STEPS.map((s) => (
            <div
              key={s.n}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                s.n <= step ? "bg-primary" : "bg-border/70",
              )}
            />
          ))}
        </div>
      </header>

      {step === 1 ? (
        <>
          <YouStep
            fields={you}
            set={setYouField}
            mode={mode}
            onMode={setMode}
            chart={{
              pending: chart.isPending,
              error: chart.error ? chart.error.message : null,
              summary: chartSummary,
            }}
          />
          <Separator />
          <div className="flex flex-wrap items-center gap-3">
            <Button

              size="lg"
              disabled={!canLeaveStep1}
              onClick={() => setStep(2)}
            >
              Then, your company <ArrowRight />
            </Button>
            {!canLeaveStep1 && (
              <p className="text-sm text-muted-foreground">
                Your name, your city and what you are looking for — that is all
                this step needs.
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            onClick={() => setStep(1)}
          >
            <ArrowLeft /> Back to you
          </Button>

          <CompanyStep
            fields={company}
            set={setCompanyField}
            dictation={{
              available: speechAvailable,
              listening,
              toggle: toggleDictation,
            }}
          />

          <Separator />
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button

                size="lg"
                disabled={!canFinish || finishing}
                onClick={() => void finish()}
              >
                {finishing ? "Building your radar…" : "Finish"} <Check />
              </Button>
              {!canFinish && (
                <p className="text-sm text-muted-foreground">
                  Just the industry and a line about what you build — the rest can
                  wait.
                </p>
              )}
            </div>
            {finishError && (
              <p className="text-sm text-destructive">{finishError.message}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
